import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Accordion, AccordionSummary,
  AccordionDetails, Alert, Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ArrowBack as BackIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

const PLACEHOLDER_MAP = {
  classification: ['{{email_subject}}', '{{email_from}}', '{{email_date}}', '{{email_body}}', '{{email_count}}', '{{emails_json}}'],
  categorization: ['{{email_subject}}', '{{email_from}}', '{{email_date}}', '{{email_body}}', '{{categories}}'],
  pdf_categorization: ['{{page_text}}', '{{page_number}}', '{{total_pages}}', '{{categories}}', '{{account_type}}']
};

export default function AgentEditor({ agent, onSave, onCancel, readOnly = false }) {
  const [name, setName] = useState(agent.name || '');
  const [systemPrompt, setSystemPrompt] = useState(agent.systemPrompt || agent.system_prompt || '');
  const [userPromptTemplate, setUserPromptTemplate] = useState(agent.userPromptTemplate || agent.user_prompt_template || '');
  const [customRules, setCustomRules] = useState(agent.customRules || agent.custom_rules || '');
  const [saving, setSaving] = useState(false);

  const placeholders = PLACEHOLDER_MAP[agent.type] || [];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        systemPrompt,
        userPromptTemplate,
        customRules: customRules.trim() || null
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={onCancel} size="small">Back</Button>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {readOnly ? '' : 'Edit: '}{agent.name}
        </Typography>
        <Chip label={agent.type.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
        {readOnly && <Chip icon={<ViewIcon />} label="Read Only" size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
      </Box>

      <TextField
        fullWidth label="Agent Name" value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ mb: 3 }}
        disabled={readOnly}
      />

      {/* Custom Rules — primary editing field */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Custom Rules</Typography>
      <TextField
        fullWidth multiline minRows={6} maxRows={12}
        value={customRules}
        onChange={(e) => setCustomRules(e.target.value)}
        placeholder={readOnly ? '(no custom rules)' : `Add your rules here, e.g.:\n- Emails from zerodha.com → Investments\n- Swiggy, Zomato orders → Eating out & delivery\n- Amazon Pay cashback → Refunds & cashback`}
        sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        disabled={readOnly}
      />
      {!readOnly && (
        <Alert severity="info" sx={{ mb: 3, fontSize: '0.8rem' }}>
          💡 Rules are appended to the system prompt as "CUSTOM RULES:" at runtime.
          Leave empty to use the base prompt without modifications.
        </Alert>
      )}

      {/* System Prompt — advanced */}
      <Accordion sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>System Prompt (Advanced)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth multiline minRows={10} maxRows={25}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            InputProps={{ readOnly }}
          />
        </AccordionDetails>
      </Accordion>

      {/* User Prompt Template — advanced */}
      <Accordion sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>User Prompt Template {!readOnly && '(Advanced)'}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth multiline minRows={10} maxRows={25}
            value={userPromptTemplate}
            onChange={(e) => setUserPromptTemplate(e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            InputProps={{ readOnly }}
          />
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Available placeholders:
            </Typography>
            {placeholders.map(p => (
              <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }} />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onCancel}>{readOnly ? 'Back' : 'Cancel'}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
